import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import CallEndIcon from '@mui/icons-material/CallEnd';
import GroupIcon from '@mui/icons-material/Group';
import { MSN } from './msnTheme';

/**
 * Modal de chamada de vídeo em grupo.
 * Sinalização via Socket.IO (groupcall:create, groupcall:join, groupcall:leave).
 * Exibe participantes na chamada e controles básicos.
 */
export default function GroupVideoCallModal({
  open,
  groupName,
  participants = [],
  currentUserId,
  isCreator,
  localStream,
  onClose,
  onHangup,
  onMuteToggle,
  onCameraToggle
}) {
  const localVideoRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);

  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  const handleMute = () => {
    const next = !muted;
    setMuted(next);
    if (localStream) {
      localStream.getAudioTracks().forEach((t) => { t.enabled = !next; });
    }
    onMuteToggle?.(next);
  };

  const handleCamera = () => {
    const next = !cameraOff;
    setCameraOff(next);
    if (localStream) {
      localStream.getVideoTracks().forEach((t) => { t.enabled = !next; });
    }
    onCameraToggle?.(next);
  };

  const others = participants.filter((p) => p.userId !== currentUserId);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'rgba(15,23,42,0.98)',
          border: '1px solid rgba(148,163,184,0.2)',
          borderRadius: 3
        }
      }}
    >
      <DialogTitle component="div" sx={{ color: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon sx={{ color: '#818cf8' }} />
          <Typography component="span" variant="h6" sx={{ fontWeight: 600 }}>Chamada em grupo: {groupName || 'Grupo'}</Typography>
          {isCreator && (
            <Chip label="Criador" size="small" sx={{ bgcolor: 'rgba(129,140,248,0.2)', color: '#a5b4fc' }} />
          )}
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#94a3b8' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          {/* Vídeo local */}
          <Box
            sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: '#0f172a',
              width: 240,
              height: 180,
              border: '1px solid rgba(148,163,184,0.2)'
            }}
          >
            <video
              ref={localVideoRef}
              autoPlay
              muted
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <Typography variant="caption" sx={{ position: 'absolute', bottom: 4, left: 4, color: '#fff' }}>
              Você
            </Typography>
          </Box>
          {/* Placeholder para outros participantes (futuro: múltiplos vídeos WebRTC) */}
          {others.slice(0, 3).map((p) => (
            <Box
              key={p.userId}
              sx={{
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: '#1e293b',
                width: 240,
                height: 180,
                border: '1px solid rgba(148,163,184,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Box sx={{ borderRadius: 2, border: `2px solid ${MSN.statusBorder?.available || MSN.online}`, p: 0.25, overflow: 'hidden', display: 'inline-block' }}>
                <Avatar sx={{ width: 64, height: 64, bgcolor: 'rgba(129,140,248,0.3)', color: '#c7d2fe' }}>
                  {(p.userName || '?').charAt(0).toUpperCase()}
                </Avatar>
              </Box>
              <Typography variant="caption" sx={{ position: 'absolute', bottom: 4, left: 4, color: '#fff' }}>
                {p.userName || 'Participante'}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography variant="subtitle2" sx={{ color: 'rgba(148,163,184,0.9)', mb: 1 }}>
          Na chamada ({participants.length})
        </Typography>
        <List dense sx={{ maxHeight: 120, overflow: 'auto', bgcolor: 'rgba(30,41,59,0.3)', borderRadius: 1 }}>
          {participants.map((p) => (
            <ListItem key={p.userId}>
              <ListItemAvatar>
                <Box sx={{ borderRadius: 1.5, border: `2px solid ${MSN.statusBorder?.available || MSN.online}`, p: 0.15, overflow: 'hidden', display: 'inline-block' }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(129,140,248,0.3)', fontSize: '0.875rem' }}>
                    {(p.userName || '?').charAt(0).toUpperCase()}
                  </Avatar>
                </Box>
              </ListItemAvatar>
              <ListItemText primary={p.userName || 'Participante'} secondary={p.userId === currentUserId ? 'Você' : null} sx={{ color: '#e2e8f0' }} />
            </ListItem>
          ))}
        </List>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
          <IconButton
            onClick={handleMute}
            sx={{
              bgcolor: muted ? 'rgba(239,68,68,0.2)' : 'rgba(51,65,85,0.6)',
              color: '#e2e8f0',
              '&:hover': { bgcolor: muted ? 'rgba(239,68,68,0.3)' : 'rgba(51,65,85,0.8)' }
            }}
          >
            {muted ? <MicOffIcon /> : <MicIcon />}
          </IconButton>
          <IconButton
            onClick={handleCamera}
            sx={{
              bgcolor: cameraOff ? 'rgba(239,68,68,0.2)' : 'rgba(51,65,85,0.6)',
              color: '#e2e8f0',
              '&:hover': { bgcolor: cameraOff ? 'rgba(239,68,68,0.3)' : 'rgba(51,65,85,0.8)' }
            }}
          >
            {cameraOff ? <VideocamOffIcon /> : <VideocamIcon />}
          </IconButton>
          <IconButton
            onClick={onHangup}
            sx={{
              bgcolor: 'rgba(239,68,68,0.3)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(239,68,68,0.5)' }
            }}
          >
            <CallEndIcon />
          </IconButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
